<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { api, type CandleData } from '../api/client'
import { sparkSegments } from './sparkline'

const props = withDefaults(defineProps<{
  fundReturnPct: number | null
  nasReturnPct: number | null
  vix: number | null
  gbpusdReturnPct: number | null
  fundLabel?: string
  // Optional explicit sparkline series for the fund line. BGA leaves this
  // undefined and the component extracts c.bga.close from CandleData (today's
  // behaviour). JH passes its NAV series (from /api/jh/nav) so the same
  // component renders the JH sparkline without a BGA-specific data dependency.
  fundSparkValues?: number[]
}>(), {
  fundLabel: 'BGA',
})

function goChart(path: string) {
  if (window.location.pathname === path) return
  window.history.pushState({}, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

const candles = ref<CandleData[]>([])
const loading = ref(true)

// Sparklines need at least 2 points to render a line. A 24h window after a
// weekend or bank-holiday gap can return 0-1 rows (last Fri flush + no new
// flushes over Sat/Sun). Fall back to the 5d window in that case and trim
// to the last 24 rows so the display stays "recent" rather than "five days".
const MIN_SPARK_POINTS = 2
const TAIL_FALLBACK = 24

onMounted(async () => {
  try {
    const d = await api.chartSnapshots('24h')
    if (d.candles.length >= MIN_SPARK_POINTS) {
      candles.value = d.candles
    } else {
      const wide = await api.chartSnapshots('5d')
      candles.value = wide.candles.slice(-TAIL_FALLBACK)
    }
  } catch { /* no data yet */ }
  loading.value = false
})

const hasCandles = computed(() => candles.value.length > 0)

// Bloomberg-style per-segment coloring: each line segment between two
// consecutive data points is colored by the direction of that step.
// `upIsGood=true` for assets where rising is positive (BGA, NAS100).
// `upIsGood=false` for "risk/pain" indicators where rising is negative
// (VIX, GBPUSD — GBP appreciation hurts a GBP-accounted US-equity book).
// Per-segment direction colouring moved to sparkline.ts (2026-11-02): the same
// function now draws this bar, GoldSparklineBar, GgeClMiniChart and the FOF home.

// Fund sparkline: prefer explicit `fundSparkValues` prop (JH path) — fall
// back to extracting from CandleData (BGA path; existing behaviour). The
// fallback preserves byte-identical BGA rendering pre/post-refactor.
const fundSpark = computed(() => {
  const closes = props.fundSparkValues != null
    ? props.fundSparkValues.filter(v => v > 0)
    : candles.value.map(c => c.bga.close).filter(v => v > 0)
  return { segments: sparkSegments(closes, 80, 18, /* upIsGood */ true), values: closes }
})

const nasSpark = computed(() => {
  const closes = candles.value.map(c => c.nas100.close)
  return { segments: sparkSegments(closes, 80, 18, /* upIsGood */ true), values: closes }
})

const vixSpark = computed(() => {
  const closes = candles.value.map(c => c.vix.close)
  return { segments: sparkSegments(closes, 80, 18, /* upIsGood */ false), values: closes }
})

const gbpusdSpark = computed(() => {
  const closes = candles.value.map(c => c.gbpusd?.close).filter((v): v is number => v != null && v > 0)
  return { segments: sparkSegments(closes, 80, 18, /* upIsGood */ false), values: closes }
})

// Returns are pre-computed in Dashboard — just display them
function fmtPct(v: number | null): string {
  if (v == null) return '—'
  return (v >= 0 ? '+' : '') + v.toFixed(2) + '%'
}

function returnClass(v: number | null): string {
  if (v == null) return ''
  return v >= 0 ? 'positive' : 'negative'
}

const liveVix = computed(() => props.vix?.toFixed(2) ?? '—')

// Route the fund + NAS spark-items to the appropriate two-line chart for
// the active portfolio: JH → /charts/jhvsnas, BGA → /charts/bgavsnas.
// (Gold-side sparkline uses its own routing in GoldSparklineBar.vue.)
const fundChartPath = computed(() =>
  props.fundLabel === 'JH' ? '/charts/jhvsnas' : '/charts/bgavsnas'
)
</script>

<template>
  <div v-if="!loading" class="sparkline-bar">
    <!-- Fund (BGA by default, JH on jh.buxushedgefund.com) — up=green, down=red (long is good) -->
    <div class="spark-item" @click="goChart(fundChartPath)">
      <span class="spark-label">{{ props.fundLabel }}</span>
      <svg v-if="(hasCandles || props.fundSparkValues != null) && fundSpark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in fundSpark.segments" :key="`f${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value" :class="returnClass(props.fundReturnPct)">{{ fmtPct(props.fundReturnPct) }}</span>
    </div>

    <!-- NAS100 — up=green, down=red -->
    <div class="spark-item" @click="goChart(fundChartPath)">
      <span class="spark-label nas">NAS</span>
      <svg v-if="hasCandles && nasSpark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in nasSpark.segments" :key="`n${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value" :class="returnClass(props.nasReturnPct)">{{ fmtPct(props.nasReturnPct) }}</span>
    </div>

    <!-- VIX — up=red (fear rising), down=green -->
    <div class="spark-item" @click="goChart('/charts/vix')">
      <span class="spark-label vix">VIX</span>
      <svg v-if="hasCandles && vixSpark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in vixSpark.segments" :key="`v${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value vix-val">{{ liveVix }}</span>
    </div>

    <!-- GBPUSD — up=red (GBP strong hurts GBP-accounted US book), down=green -->
    <div class="spark-item" @click="goChart('/charts/gbpusd')">
      <span class="spark-label fx">FX</span>
      <svg v-if="hasCandles && gbpusdSpark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in gbpusdSpark.segments" :key="`f${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value" :class="returnClass(props.gbpusdReturnPct)">{{ fmtPct(props.gbpusdReturnPct) }}</span>
    </div>

  </div>
</template>

<style scoped>
.sparkline-bar {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
  gap: 16px;
  padding: 6px 12px;
  background: #0d1117;
  border: 1px solid #1c2128;
  border-radius: 6px;
  margin-bottom: 8px;
}
.spark-item { display: flex; align-items: center; gap: 6px; min-width: 0; cursor: pointer; padding: 2px 4px; border-radius: 4px; }
.spark-item:hover { background: #161b22; }
.spark-svg { flex: 1 1 auto; min-width: 0; width: 100%; height: 18px; }
.spark-label { font-size: 10px; color: #4caf50; font-family: 'JetBrains Mono', monospace; min-width: 24px; flex: 0 0 auto; }
.spark-label.nas { color: #2196f3; }
.spark-label.vix { color: #fbc02d; }
.spark-label.fx { color: #26c6da; }
.spark-value { font-size: 10px; font-family: 'JetBrains Mono', monospace; color: #888; min-width: 40px; flex: 0 0 auto; }
.spark-value.positive { color: #4caf50; }
.spark-value.negative { color: #f44336; }
.spark-value.bga-val { color: #4caf50; }
.spark-value.nas-val { color: #2196f3; }
.spark-value.vix-val { color: #fbc02d; }

@media (max-width: 768px) {
  .sparkline-bar { grid-template-columns: repeat(2, 1fr); gap: 8px; padding: 4px 8px; }
  .spark-label { font-size: 9px; }
  .spark-value { font-size: 9px; }
}
</style>
